import { ApolloLink, Observable } from 'apollo-link'

// Answers of the proxy while the backend is not reachable, e.g. during a restart
const TRANSIENT_STATUS_CODES = [502, 503, 504]

const DEFAULT_DELAYS = [1000, 2000, 4000, 8000]

// A failed fetch has no status code, e.g. right after the device woke up and before the network is back
function isConnectionError (error) {
  return error != null && error.statusCode == null
}

function isMutation (operation) {
  return operation.query.definitions.some(definition =>
    definition.kind === 'OperationDefinition' && definition.operation === 'mutation'
  )
}

function wait (milliseconds) {
  return new Promise(resolve => setTimeout(resolve, milliseconds))
}

/**
 * Repeats operations that failed for a short time instead of failing at once.
 *
 * - Connection errors and 502/503/504 are repeated after each of the [delays]. Mutations are not repeated after a
 *   connection error, since they may have reached the server and would be recorded twice.
 * - A rejected access token (401) is refreshed with [refreshAccess] and the operation repeated once. It resolves to
 *   false if the refresh token was rejected as well.
 *
 * Put the link before the link that sets the access token, so a repeated operation gets the refreshed one.
 */
function createRetryLink ({ refreshAccess, delays = DEFAULT_DELAYS, sleep = wait }) {
  return new ApolloLink((operation, forward) => new Observable(observer => {
    let subscription = null
    let closed = false
    let attempt = 0
    let refreshed = false

    function nextAttempt (error) {
      if (error.statusCode === 401) {
        if (refreshed) return null
        refreshed = true
        return refreshAccess().then(accepted => {
          if (!accepted) throw error
        })
      }
      const transient = TRANSIENT_STATUS_CODES.includes(error.statusCode) ||
        (isConnectionError(error) && !isMutation(operation))
      if (!transient || attempt >= delays.length) return null
      return sleep(delays[attempt++])
    }

    function start () {
      subscription = forward(operation).subscribe({
        next: value => observer.next(value),
        complete: () => observer.complete(),
        error: error => {
          const next = closed ? null : nextAttempt(error)
          if (next == null) {
            observer.error(error)
            return
          }
          next.then(
            () => { if (!closed) start() },
            () => observer.error(error)
          )
        }
      })
    }

    start()

    return () => {
      closed = true
      if (subscription) subscription.unsubscribe()
    }
  }))
}

export { createRetryLink }
